// ** React Imports
import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import LocationAutocomplete from "components/layout/LocationAutocomplete";

// ** Custom Hooks
import useQuery from "hooks/useQuery";
import filterDistanceComputeService from "hooks/filters/compute/useFilterDistanceCompute";

// ** Utils & Helpers
import generateQueryPath from "utils/generateQueryPath";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const useNearbyLocationSelector = ({ cityName, regionName, countryCode }) => {
	const { t } = useTranslation("common");
	const { push } = useHistory();
	const query = useQuery();

	const { isPhone } = useContext(deviceTypeContext);

	const userCityName = useSelector((state) => state.globalUser.cityName);
	const userCityId = useSelector((state) => state.globalUser.city);
	const userRegionName = useSelector((state) => state.globalUser.regionName);

	// ** Parametry z search query
	const {
		country: countryUrl,
		region: regionUrl,
		city: cityUrl,
		distance: distanceUrl,
	} = query;

	const distanceApiParam =
		filterDistanceComputeService().urlToApiParam(distanceUrl) ?? null;

	const [selected, setSelected] = useState({
		country: countryUrl,
		countryName: countryUrl === "all" ? "" : null,
		regionId: regionUrl,
		regionName: userRegionName,
		cityId: cityUrl,
		cityName: userCityName,
		distance:
			distanceApiParam === 0
				? 0
				: distanceApiParam
				? distanceApiParam
				: null,
	});

	useEffect(() => {
		setSelected({
			country: countryUrl,
			countryName: countryUrl === "all" ? "" : null,
			regionName: regionName,
			regionId: regionUrl,
			cityName: cityName,
			cityId: cityUrl || userCityId,
			distance:
				distanceApiParam === 0
					? 0
					: distanceApiParam
					? distanceApiParam
					: null,
		});
	}, [regionName, cityName, countryCode, distanceUrl]);

	const customWidth = cityUrl !== "any" ? 301 : 407;

	const renderLocationSelector = (setFiltersOpen) => (
		<LocationAutocomplete
			location={selected}
			onAccept={(data) => {
				const { regionId, country, cityId, distance } = data;

				const to = generateQueryPath(`/nearby`, {
					...query,
					country: country || "all",
					region: regionId || "any",
					city: cityId || "any",
					distance: distance === 0 ? 0 : distance ? distance : null,
				});

				push(to);
				if (typeof setFiltersOpen === "function") {
					setFiltersOpen(false);
				}
			}}
			label={t("filtersAndSelectors:locationLabel")}
			withDistance
			withLabel
			noOther
			minWidth={isPhone ? 200 : customWidth ? customWidth : 307}
			withGeolocation
			noPad
			extend
		/>
	);

	const filterLocationData = {
		label: t("filtersAndSelectors:locationLabel"),
		selected,
	};

	// #####################################################

	return { renderLocationSelector, filterLocationData };
};

export default useNearbyLocationSelector;

// #####################################################
